import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import CustomButton from '../reusable/CustomButton';
import PageHeader from '../reusable/PageHeader';
import Panel from '../reusable/Panel';
import Loader from '../reusable/Loader';
import SelectDropdown from '../reusable/SelectDropdown';
import Tag from '../reusable/Tag';
import { AuthorTag } from '../reusable/Comments';
import useUserDetails from '../hooks/useUserDetails';
import useSchools from '../hooks/useSchools';
import useErrorModal from '../hooks/useErrorModal';
import type { SchoolFocusGroupReport } from '../../services/focusGroupService';
import {
  FOCUS_GROUP_REPORT_POC_PATH,
  GeneratedQuote,
  GeneratedReport,
  GenerationDetail,
  GenerationSummary,
  getGeneration,
  getProductionFocusGroupReport,
  listGenerations,
  postGenerateStream,
} from './generationService';
import './FocusGroupReportPocPage.css';

export { FOCUS_GROUP_REPORT_POC_PATH };

const TOOL_LABELS: Record<string, string> = {
  list_focus_group_transcriptions: 'Listing transcriptions…',
  get_focus_group_transcription: 'Reading a transcription…',
};

const statusTagType = (status: string): 'pending' | 'progress' | 'completed' | 'error' => {
  if (status === 'COMPLETED') return 'completed';
  if (status === 'FAILED') return 'error';
  return 'progress';
};

const verdictTagType = (verdict: string | null | undefined): 'pending' | 'progress' | 'completed' | 'error' => {
  if (verdict === 'verified') return 'completed';
  if (verdict === 'near_miss') return 'progress';
  if (verdict === 'unverified') return 'error';
  return 'pending';
};

const PocQuote = ({ quote }: { quote: GeneratedQuote }) => (
  <li className="fg-poc-quote">
    “{quote.text}”
    <div className="fg-poc-quote-meta">
      <AuthorTag speakerId={quote.speaker_id} shape="chip" />
      <Tag
        size="small"
        type={verdictTagType(quote.verification?.verdict)}
        text={quote.verification?.verdict ?? 'unchecked'}
      />
      <span className="fg-poc-run-meta">{quote.focus_group_name}</span>
    </div>
    {quote.verification?.detail ? <div className="fg-poc-run-meta">{quote.verification.detail}</div> : null}
  </li>
);

export const PocReportBody = ({ report }: { report: GeneratedReport }) => (
  <div>
    <p style={{ whiteSpace: 'pre-wrap' }}>{report.summary}</p>
    <ol>
      {report.themes.map((theme, themeIndex) => (
        <li key={theme.name} className="fg-poc-theme">
          <h3 className="regular">{theme.name}</h3>
          <h4 id={`fg-poc-takeaways-${themeIndex}`}>Response Trends/Themes</h4>
          <ul className="nested-list" aria-labelledby={`fg-poc-takeaways-${themeIndex}`}>
            {theme.takeaways.map((takeaway, index) => (
              <li key={`${theme.name}-takeaway-${index}`}>{takeaway.content}</li>
            ))}
          </ul>
          <h4 id={`fg-poc-quotes-${themeIndex}`}>Notable student quotes</h4>
          <ul className="nested-list" aria-labelledby={`fg-poc-quotes-${themeIndex}`}>
            {theme.takeaways
              .flatMap((takeaway) => takeaway.quotes)
              .map((quote, quoteIndex) => (
                <PocQuote key={`${quote.text}-${quoteIndex}`} quote={quote} />
              ))}
          </ul>
        </li>
      ))}
    </ol>
  </div>
);

const LambdaReportBody = ({ report }: { report: SchoolFocusGroupReport }) => (
  <div>
    <p style={{ whiteSpace: 'pre-wrap' }}>{report.summary || 'No summary.'}</p>
    <ol>
      {report.themes?.map((theme) => (
        <li key={theme.id || theme.name} className="fg-poc-theme">
          <h3 className="regular">{theme.name}</h3>
          <ul>
            {theme.takeaways.map((takeaway) => (
              <li key={takeaway}>{takeaway}</li>
            ))}
          </ul>
          <ul>
            {theme.quotes.map((quote) => (
              <li key={quote.id} className="fg-poc-quote">
                “{quote.text}”
                <div className="fg-poc-quote-meta">
                  <AuthorTag speakerId={quote.speaker_id} shape="chip" />
                </div>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  </div>
);

const FocusGroupReportPocPage = () => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const {
    schools: { data: schools, isPending: schoolsPending },
  } = useSchools();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [schoolId, setSchoolId] = useState('');
  const [runs, setRuns] = useState<GenerationSummary[]>([]);
  const [selected, setSelected] = useState<GenerationDetail | null>(null);
  const [productionReport, setProductionReport] = useState<SchoolFocusGroupReport | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const eoaId = userDetails?.eoaId;
  const accessToken = userDetails?.access_token;

  useEffect(() => {
    if (!schoolId && schools?.length) setSchoolId(schools[0].id);
  }, [schools, schoolId]);

  const refreshRuns = useCallback(
    async (selectId?: string) => {
      if (!eoaId || !accessToken || !schoolId) return;
      setLoadingList(true);
      try {
        const [generations, production] = await Promise.all([
          listGenerations(eoaId, schoolId, accessToken),
          getProductionFocusGroupReport(eoaId, schoolId, accessToken),
        ]);
        setRuns(generations);
        setProductionReport(production);
        const idToLoad = selectId ?? generations[0]?.id;
        if (!idToLoad) {
          setSelected(null);
          return;
        }
        setSelected(await getGeneration(eoaId, schoolId, idToLoad, accessToken));
      } catch (error) {
        handleOpenErrorModal('Failed to load focus group report generations', error as Error);
      } finally {
        setLoadingList(false);
      }
    },
    [eoaId, accessToken, schoolId, handleOpenErrorModal]
  );

  useEffect(() => {
    abortRef.current?.abort();
    setLog([]);
    setGenerating(false);
    void refreshRuns();
  }, [refreshRuns]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const selectRun = async (generationId: string) => {
    if (!eoaId || !accessToken || !schoolId) return;
    try {
      setSelected(await getGeneration(eoaId, schoolId, generationId, accessToken));
    } catch (error) {
      handleOpenErrorModal('Failed to load generation', error as Error);
    }
  };

  const generate = async () => {
    if (!eoaId || !accessToken || !schoolId || generating) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setGenerating(true);
    setLog(['Starting generation…']);
    try {
      const done = await postGenerateStream(eoaId, schoolId, accessToken, {
        signal: controller.signal,
        onThinking: (text) =>
          setLog((prev) => {
            const last = prev[prev.length - 1];
            if (last?.startsWith('Thinking: ')) return [...prev.slice(0, -1), last + text];
            return [...prev, `Thinking: ${text}`];
          }),
        onToolCall: (toolName) => setLog((prev) => [...prev, TOOL_LABELS[toolName] ?? `Using ${toolName}…`]),
        onVerification: (round, quotes) => {
          const counts = quotes.reduce<Record<string, number>>((acc, quote) => {
            const key = quote.verdict ?? 'unchecked';
            acc[key] = (acc[key] ?? 0) + 1;
            return acc;
          }, {});
          const summary = Object.entries(counts)
            .map(([verdict, count]) => `${count} ${verdict}`)
            .join(', ');
          setLog((prev) => [...prev, `Verification round ${round}: ${summary}`]);
        },
      });
      setLog((prev) => [...prev, `Finished: ${done.status}${done.error ? ` — ${done.error}` : ''}`]);
      await refreshRuns(done.generation_id);
    } catch (error) {
      if (!controller.signal.aborted) {
        handleOpenErrorModal('Focus group report generation failed', error as Error);
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (!controller.signal.aborted) setGenerating(false);
    }
  };

  if (!userDetails || schoolsPending) return <Loader />;

  return (
    <div className="main-content">
      <PageHeader
        title="Focus group report POC"
        pageDescription="Throwaway SuperAdmin review UI. Writes only to focus_group_report_generations — facilitators never see these runs."
      />

      <div className="fg-poc-toolbar">
        <SelectDropdown
          label="School"
          selectedValue={schoolId}
          handleChange={(event) => setSchoolId(event.target.value)}
          items={(schools ?? []).map((school) => ({ label: school.name, value: school.id }))}
          isClearable={false}
          formControlVariant="outlined"
        />
        <CustomButton
          variant="primary"
          buttonText="Generate"
          buttonAction={() => void generate()}
          loading={generating}
          disabled={!schoolId}
        />
      </div>

      <div className="fg-poc-columns">
        <Panel>
          <h2 className="regular">Runs</h2>
          {loadingList ? (
            <Loader />
          ) : runs.length === 0 ? (
            <p className="fg-poc-empty">No generations for this school yet.</p>
          ) : (
            <div className="fg-poc-runs">
              {runs.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  className={`fg-poc-run${selected?.id === run.id ? ' selected' : ''}`}
                  onClick={() => void selectRun(run.id)}
                >
                  <Tag size="small" type={statusTagType(run.status)} text={run.status} />
                  <div className="fg-poc-run-meta">{dayjs(run.created_at).format('MM/DD/YYYY hh:mma')}</div>
                  <div className="fg-poc-run-meta">{run.prompt_hash.slice(0, 8)}</div>
                </button>
              ))}
            </div>
          )}
        </Panel>

        <Panel>
          <h2 className="regular">Selected run</h2>
          {generating || log.length > 0 ? <pre className="fg-poc-log">{log.join('\n')}</pre> : null}
          {!selected && !generating ? (
            <p className="fg-poc-empty">Generate a report or pick a run.</p>
          ) : selected ? (
            <>
              <Tag size="small" type={statusTagType(selected.status)} text={selected.status} />
              <p className="fg-poc-run-meta">
                {selected.model_id} · {selected.prompt_hash.slice(0, 8)} ·{' '}
                {dayjs(selected.created_at).format('MM/DD/YYYY hh:mma')}
              </p>
              {selected.error ? <p>{selected.error}</p> : null}
            </>
          ) : null}
        </Panel>
      </div>

      <div className="fg-poc-compare">
        <Panel>
          <h2 className="regular">POC report</h2>
          {selected?.result ? (
            <PocReportBody report={selected.result} />
          ) : (
            <p className="fg-poc-empty">No POC report body for this run.</p>
          )}
        </Panel>
        <Panel>
          <h2 className="regular">Current pipeline report</h2>
          {productionReport ? (
            <LambdaReportBody report={productionReport} />
          ) : (
            <p className="fg-poc-empty">No production focus group report for this school.</p>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default FocusGroupReportPocPage;
